/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "dbt observability layer", description = "See which Hightouch syncs are using your dbt models from your dbt docs and lineage graph.", lastUpdatedDate = new Date(1787702400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "dbt observability layer",
        description: "See which Hightouch syncs are using your dbt models from your dbt docs and lineage graph.",
        lastUpdatedDate: "Wed Aug 26 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "dbt CI checks",
          "depth": 0,
          "id": "dbt-ci-checks"
        }, {
          "value": "dbt exposure sync",
          "depth": 0,
          "id": "dbt-exposure-sync"
        }, {
          "value": "Turn on exposure sync",
          "depth": 1,
          "id": "turn-on-exposure-sync"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Git credentials",
          "depth": 1,
          "id": "git-credentials"
        }, {
          "value": "dbt build errors",
          "depth": 1,
          "id": "dbt-build-errors"
        }, {
          "value": "Your local changes to the following files would be overwritten by checkout",
          "depth": 1,
          "id": "your-local-changes-to-the-following-files-would-be-overwritten-by-checkout"
        }, {
          "value": "Remote rejected, protected branch hook declined",
          "depth": 1,
          "id": "remote-rejected-protected-branch-hook-declined"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      ul: "ul",
      li: "li",
      a: "a",
      h2: "h2",
      strong: "strong",
      img: "img",
      h3: "h3",
      code: "code"
    }, _provideComponents(), props.components), {Video, PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!Video) _missingMdxReference("Video", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Hightouch provides two separate features to monitor your dbt models as they relate to Hightouch syncs:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#dbt-ci-checks",
            children: "dbt CI checks"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#dbt-exposure-sync",
            children: "dbt exposure sync"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can use either one or both of these features together. Both require that you've ", _jsx(_components.a, {
          href: "/extensions/dbt-models",
          children: "set up dbt-based models"
        }), " in Hightouch."]
      }), "\n", _jsx(_components.h2, {
        id: "dbt-ci-checks",
        children: "dbt CI checks"
      }), "\n", _jsx(Video, {
        source: "https://cdn.sanity.io/files/pwmfmi47/production/8e2d00e0f289dc7341537dd68b8a2ce4fa251841.mp4",
        poster: "https://cdn.sanity.io/images/pwmfmi47/production/30fa52a12054367ea83d598a9d61bf5d9b397d52-5120x2880.webp",
        title: "Learn about dbt CI Checks",
        duration: "3 min",
        publishedAt: "2022-10-18T08:00:00+08:00"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The dbt CI checks feature doesn't currently work with ", _jsx(_components.a, {
            href: "/customer-studio/overview",
            children: "Customer\nStudio"
          }), " models."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can use Hightouch's ", _jsx(_components.strong, {
          children: "dbt CI checks"
        }), " to validate changes in your dbt project Git repository. This feature checks your GitHub pull requests to ensure they don't break any existing, ", _jsx(_components.a, {
          href: "/syncs/overview#individual-sync-overview-page",
          children: "enabled"
        }), " dbt-based syncs in Hightouch."]
      }), "\n", _jsxs(_components.p, {
        children: ["To enable this, toggle on ", _jsx(_components.strong, {
          children: "GitHub Pull Request checks"
        }), " from your dbt connected source's ", _jsx(_components.a, {
          href: "https://app.hightouch.com/extensions/dbt-models/configuration?from=docs",
          children: "configuration page"
        }), " and then select ", _jsx(_components.strong, {
          children: "Save"
        }), ". You need to do this for every connected source you want to enable CI checking on."]
      }), "\n", _jsx(_components.p, {
        children: "Once enabled, Hightouch runs a CI check on any pull request made to the dbt project's Git repository. If a breaking change is detected—for example, deleting, renaming, or moving a model used in a sync—the check fails."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/dbt-failed-code-check.png",
          alt: "Failed dbt CI check"
        })
      }), "\n", _jsx(_components.p, {
        children: "Clicking into the details of a failed CI check shows which sync the breaking change effects."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/dbt-code-checks-broken-sync.png",
          alt: "Failed dbt CI check details"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The CI check passes if the pull request doesn't affect any ", _jsx(_components.a, {
          href: "/syncs/overview#individual-sync-overview-page",
          children: "enabled"
        }), " Hightouch syncs."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Though dbt CI checks work similarly to ", _jsx(_components.a, {
            href: "/extensions/git-sync#validate-changes",
            children: "version control with Git\nSync"
          }), ", these are separate features and\naren't dependent on one another."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "dbt-exposure-sync",
        children: "dbt exposure sync"
      }), "\n", _jsx(Video, {
        source: "https://cdn.sanity.io/files/pwmfmi47/production/49b8cec8d4a0f1e642e8ebaf01bd46abdf99333e.mp4",
        poster: "https://cdn.sanity.io/images/pwmfmi47/production/30fa52a12054367ea83d598a9d61bf5d9b397d52-5120x2880.webp",
        title: "Learn about dbt exposures",
        duration: "3 min",
        publishedAt: "2022-08-02T08:00:00+08:00"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch exposure sync lets you see which Hightouch syncs are using your dbt models via your dbt docs and lineage graph."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/exposures-lineage-before.png",
          alt: "dbt Lineage graph without exposure sync",
          title: "dbt Lineage graph without exposure sync"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/exposures-lineage-graph.png",
          alt: "dbt Lineage graph with exposure sync",
          title: "dbt Lineage graph with exposure sync enabled"
        })
      }), "\n", _jsx(_components.p, {
        children: "Exposure sync works by defining and updating exposures in your chosen dbt repository anytime a Hightouch sync that uses a dbt model is added or modified."
      }), "\n", _jsx(_components.h3, {
        id: "turn-on-exposure-sync",
        children: "Turn on exposure sync"
      }), "\n", _jsxs(_components.p, {
        children: ["You can enable exposure sync by navigating to ", _jsx(_components.strong, {
          children: "dbt Models"
        }), " on the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/extensions?from=docs",
          children: _jsx(_components.strong, {
            children: "Extensions page"
          })
        }), " and selecting your ", _jsx(_components.strong, {
          children: "dbt Connected Source"
        }), ". From there, activate the ", _jsx(_components.strong, {
          children: "dbt Exposures Sync"
        }), " toggle and click ", _jsx(_components.strong, {
          children: "Save"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "You should see exposures starting to sync within a few minutes."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/dbt-enable-exposures.png",
          alt: "Turning on dbt Exposure Sync in Hightouch"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["In your dbt repository, you should see a new file called ", _jsx(_components.code, {
          children: "hightouch_sync_exposures.yml"
        }), ". This file updates every time a Hightouch sync that uses a dbt model is added, modified or disabled.\n", _jsx(_components.img, {
          src: "integrations/exposures-commit.png",
          alt: "GitHub commit screenshot"
        })]
      }), "\n", _jsxs(Tip, {
        children: [_jsxs(_components.p, {
          children: ["Exposures will be created for syncs using audiences created with Customer\nStudio where the ", _jsx(_components.a, {
            href: "/customer-studio/schema#create-the-parent-model",
            children: "parent\nmodel"
          }), " is a dbt model. If an\naudience references ", _jsx(_components.a, {
            href: "/customer-studio/schema#add-a-related-model",
            children: "related or event\nmodels"
          }), " that are built with dbt\nmodels, this information won't be included in the exposure file. If you'd like\nto request this functionality, please", " "]
        }), _jsx(PylonLink, {
          pylonText: "Hello, I would like to request support for adding related and event model information to dbt exposures.",
          linkText: "reach out to customer support"
        }), _jsx(_components.p, {
          children: "."
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "git-credentials",
        children: "Git credentials"
      }), "\n", _jsxs(_components.p, {
        children: ["If your Git credentials are out of date, you won't be able to connect your dbt models and analyses. We recommend using ", _jsx(_components.a, {
          href: "https://github.com/apps/hightouch-connect/",
          children: "Hightouch's GitHub app"
        }), " for your credentials if you're using GitHub."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-credential-error.png",
          alt: "Git credential error"
        })
      }), "\n", _jsx(_components.p, {
        children: "The Hightouch GitHub app must have commit access to your chosen dbt branch to create exposures. If Hightouch is unable to push to your branch you may have to update your branch protection rules in your dbt GitHub repository."
      }), "\n", _jsx(_components.h3, {
        id: "dbt-build-errors",
        children: "dbt build errors"
      }), "\n", _jsx(_components.p, {
        children: "The Hightouch exposures file is intended to stop you from breaking Hightouch syncs as a result of deleted dbt models. If you need to delete a model defined in the Hightouch exposures file, you can remove the Hightouch exposures file at any point—Hightouch will automatically recreate it."
      }), "\n", _jsx(_components.h3, {
        id: "your-local-changes-to-the-following-files-would-be-overwritten-by-checkout",
        children: "Your local changes to the following files would be overwritten by checkout"
      }), "\n", _jsxs(_components.p, {
        children: ["This error generally occurs if the ", _jsx(_components.strong, {
          children: "exposure sync branch"
        }), " in your linked Git repository has diverged from your main branch."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-branches.png",
          alt: "Git branches"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Once you update your exposure branch so that it's up-to-date with your main one, it should resolve the error. You can find more information on how you can resolve this type of error in this ", _jsx(_components.a, {
          href: "https://stackoverflow.com/questions/22424142/error-your-local-changes-to-the-following-files-would-be-overwritten-by-checkou",
          children: "Stack Overflow article"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "remote-rejected-protected-branch-hook-declined",
        children: "Remote rejected, protected branch hook declined"
      }), "\n", _jsxs(_components.p, {
        children: ["You may receive this error if you have ", _jsx(_components.a, {
          href: "https://docs.github.com/en/repositories/configuring-branches-and-merges-in-your-repository/managing-protected-branches/about-protected-branches",
          children: "branch protections"
        }), " on the ", _jsx(_components.strong, {
          children: "branch"
        }), " you selected for you ", _jsx(_components.a, {
          href: "#turn-on-exposure-sync",
          children: "exposure sync configuration"
        }), ".\nTo avoid this, you can select a branch to use for exposure syncs specifically."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/exposure-sync-branch.png",
          alt: "Git branches"
        })
      }), "\n", _jsx(_components.p, {
        children: "Then, you can open a pull request from the dbt exposure sync branch to your main dbt branch.\nThis allows the exposure sync with Hightouch to operate smoothly while also allowing branch protection rules to be applied to the main branch."
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["You can read more about a similar error in ", _jsx(_components.a, {
            href: "/extensions/git-sync#protected-branch-update-failed",
            children: "this\nsection"
          }), " of the Git Sync\npage."]
        })
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
